import { Attempt } from "@nicsmith/shared_types";
import { baseApi } from "./api";

interface ModuleAttemptsSummary {
  attemptedExercisesCount: number;
  totalExercisesCount: number;
  totalBestResults: number;
}

export const attemptApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch an attempt for the current user by exercise ID
    getLatestAttemptForExercise: builder.query<Attempt, string>({
      query: (exerciseId) => `/attempts/exercise/${exerciseId}`,
    }),

    getAllAttemptsForExercise: builder.query<Attempt[], string>({
      query: (exerciseId) => `/attempts/exercise/${exerciseId}/all`,
      providesTags: (result, error, exerciseId) => [
        { type: "Attempt", id: exerciseId },
        { type: "Attempt", id: "LIST" },
      ],
    }),

    // Create a new attempt for the current user
    createAttempt: builder.mutation<
      Attempt,
      { exerciseId: string; attemptData: Partial<Attempt> }
    >({
      query: ({ exerciseId, attemptData }) => ({
        url: `/attempts/exercise/${exerciseId}`,
        method: "POST",
        body: attemptData,
      }),
      invalidatesTags: (result, error, { exerciseId }) => [
        { type: "Attempt", id: exerciseId },
        { type: "Attempt", id: "LIST" },
        "Course",
        "UserDetail",
      ],
    }),

    // Update an existing attempt
    updateAttempt: builder.mutation<
      Attempt,
      { attemptId: string; attemptData: Partial<Attempt> }
    >({
      query: ({ attemptId, attemptData }) => ({
        url: `/attempts/${attemptId}`,
        method: "PUT",
        body: attemptData,
      }),
      invalidatesTags: (result, error, { attemptId }) => [
        { type: "Attempt", id: attemptId },
        { type: "Attempt", id: "LIST" },
        "Course",
      ],
    }),

    // Delete an attempt by attempt ID
    deleteAttempt: builder.mutation<void, string>({
      query: (attemptId) => ({
        url: `/attempts/${attemptId}`,
        method: "DELETE",
      }),
    }),

    // New: Fetch all attempts for a user in a specific module
    getModuleAttempts: builder.query<Attempt[], string>({
      query: (moduleId) => `/attempts/module/${moduleId}`,
      providesTags: (result, error, moduleId) => [
        { type: "Attempt", id: moduleId },
        { type: "Attempt", id: "LIST" },
      ],
    }),

    // New: Get attempt summary for a user in a specific module
    getModuleAttemptsSummary: builder.query<ModuleAttemptsSummary, string>({
      query: (moduleId) => `/attempts/module/${moduleId}/summary`,
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in components
export const {
  useGetLatestAttemptForExerciseQuery,
  useGetAllAttemptsForExerciseQuery,
  useCreateAttemptMutation,
  useUpdateAttemptMutation,
  useDeleteAttemptMutation,
  useGetModuleAttemptsQuery,
  useGetModuleAttemptsSummaryQuery,
} = attemptApi;
